import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Row } from 'antd'
import {
  filter,
  includes,
  intersection,
  isEmpty,
  map,
  noop,
  replace,
  size,
  uniq,
} from 'lodash'
import PropTypes from 'prop-types'

import { DirtyPrompt } from '../../../core/components/sections'
import { usePrevious } from '../../../core/hooks'

import { ALLOWED_METADATA_TREE_OPERATIONS } from '../../constants'
import {
  addMetadataTreeRoot,
  filterParentKeysWhenNotAllChildrenSelected,
} from '../../helpers'
import { nodePropType } from '../../prop-types'
import { flattenTreeStructure } from '../../utils'
import { StyledVerticalSpace } from '../styled'

import MetadataNodeTitle from './metadata-node-title'
import MetadataTreeEditableArea from './metadata-tree-editable-area'
import SearchableMetadataTree from './searchable-metadata-tree'

const MetadataTree = ({
  disableLink,
  mainTreeExpandedKeys,
  supportingTreeExpandedKeys,
  onMainTreeExpand,
  onSupportingTreeExpand,
  metadataType,
  secondaryMetadataType,
  isDirty,
  showSearch,
  treeData,
  editable,
  addNode,
  deleteNode,
  renameNode,
  setDataForRenaming,
  setIsCurationEnabled,
  dataForRenaming,
  allowReplaceMany,
  moveNodes,
  replaceNode,
  revertReplaceNode,
  computeNodeHighlight,
  nodeTitleContentRender,
  showRenameInput,
  shouldPickSelectedSupportingItem,
  isMoveActive,
  setIsMoveActive,
  isReplaceActive,
  setIsReplaceActive,
  getLink,
}) => {
  const [mainTreeCheckedKeys, setMainTreeCheckedKeys] = useState([])
  const [supportingTreeCheckedKeys, setSupportingTreeCheckedKeys] = useState([])

  const previousIsMoveActive = usePrevious(isMoveActive)
  const previousIsReplaceActive = usePrevious(isReplaceActive)

  // hack to circumvent ant design tree component storing checked keys when checkable is set to false
  // at the same time when we set the checkedKeys list to []
  const [isSupportingTreeShown, setIsSupportingTreeShown] = useState(false)

  useEffect(() => {
    const isMoveOrReplaceFinishedTogglingOff =
      isEmpty(mainTreeCheckedKeys) &&
      ((previousIsMoveActive && !isMoveActive) ||
        (previousIsReplaceActive && !isReplaceActive))

    if (isMoveOrReplaceFinishedTogglingOff) {
      setIsSupportingTreeShown(false)
    }

    if (isMoveActive || isReplaceActive) {
      setIsSupportingTreeShown(true)
    }
  }, [
    isMoveActive,
    isReplaceActive,
    mainTreeCheckedKeys,
    previousIsMoveActive,
    previousIsReplaceActive,
  ])

  const flatMainTreeData = useMemo(
    () => flattenTreeStructure(treeData),
    [treeData],
  )

  const supportingTreeData = useMemo(
    () =>
      isMoveActive ? addMetadataTreeRoot(treeData, metadataType) : treeData,
    [isMoveActive, treeData, metadataType],
  )

  const flatSupportingTreeData = useMemo(
    () => flattenTreeStructure(supportingTreeData),
    [supportingTreeData],
  )

  const handleMainTreeOnCheck = useCallback(
    ({ checked }) => {
      const keyToAdd = checked[size(checked) - 1]

      const isKeyAlreadyCheckedOnSupportingTree = includes(
        supportingTreeCheckedKeys,
        keyToAdd,
      )

      if (isKeyAlreadyCheckedOnSupportingTree) {
        return
      }

      if (isReplaceActive) {
        const checkedKeys = isEmpty(checked) ? [] : [checked[size(checked) - 1]]
        setMainTreeCheckedKeys(checkedKeys)
        return
      }

      if (isMoveActive) {
        if (size(checked) < size(mainTreeCheckedKeys)) {
          const keysToCheck = filterParentKeysWhenNotAllChildrenSelected(
            checked,
            flatMainTreeData,
          )

          setMainTreeCheckedKeys(keysToCheck)
          return
        }

        const childrenNodesToCheck = filter(
          flatMainTreeData,
          ({ key, keyPath, allowedOperations }) =>
            includes(
              allowedOperations,
              ALLOWED_METADATA_TREE_OPERATIONS.MOVE,
            ) &&
            (includes(checked, key) ||
              !isEmpty(intersection(checked, keyPath))),
        )

        setMainTreeCheckedKeys(map(childrenNodesToCheck, 'key'))
      }
    },
    [
      flatMainTreeData,
      isMoveActive,
      isReplaceActive,
      mainTreeCheckedKeys,
      supportingTreeCheckedKeys,
    ],
  )

  const handleSupportingTreeOnCheck = useCallback(
    ({ checked }) => {
      let keysToCheck = checked
      const keyToAdd = checked[size(checked) - 1]

      const isKeyAlreadyCheckedOnMainTree = includes(
        mainTreeCheckedKeys,
        isMoveActive ? replace(keyToAdd, '0-', '') : keyToAdd,
      )

      const isMovingUnderneathLastLevel =
        isMoveActive &&
        !isEmpty(
          filter(
            flatSupportingTreeData,
            ({ key, level }) => keyToAdd === key && level === 4,
          ),
        )

      if (isKeyAlreadyCheckedOnMainTree || isMovingUnderneathLastLevel) {
        return
      }

      if (isMoveActive || (isReplaceActive && !allowReplaceMany)) {
        keysToCheck = isEmpty(checked) ? [] : [keyToAdd]
      }

      setSupportingTreeCheckedKeys(keysToCheck)
    },
    [
      mainTreeCheckedKeys,
      isMoveActive,
      flatSupportingTreeData,
      isReplaceActive,
      allowReplaceMany,
    ],
  )

  const handleAddChildNode = useCallback(
    node => {
      addNode(node)

      onMainTreeExpand(uniq([...mainTreeExpandedKeys, ...node.keyPath]))
    },
    [addNode, mainTreeExpandedKeys, onMainTreeExpand],
  )

  const renderMetadataTreeNode = useCallback(
    (node, searchValue) => (
      <MetadataNodeTitle
        metadataType={metadataType}
        searchValue={searchValue}
        node={node}
        computeNodeHighlight={computeNodeHighlight}
        editable={editable && !isSupportingTreeShown}
        disableLink={isDirty || isSupportingTreeShown || disableLink}
        isTreeDirty={isDirty}
        addNode={handleAddChildNode}
        deleteNode={deleteNode}
        renameNode={renameNode}
        revertReplaceNode={revertReplaceNode}
        setDataForRenaming={setDataForRenaming}
        setIsCurationEnabled={setIsCurationEnabled}
        dataForRenaming={dataForRenaming}
        nodeTitleContentRender={nodeTitleContentRender}
        showRenameInput={showRenameInput}
        getLink={getLink}
      />
    ),
    [
      metadataType,
      computeNodeHighlight,
      editable,
      isSupportingTreeShown,
      isDirty,
      disableLink,
      handleAddChildNode,
      deleteNode,
      renameNode,
      revertReplaceNode,
      setDataForRenaming,
      setIsCurationEnabled,
      dataForRenaming,
      nodeTitleContentRender,
      showRenameInput,
      getLink,
    ],
  )

  return (
    <StyledVerticalSpace>
      <DirtyPrompt isDirty={isDirty && !isMoveActive && !isReplaceActive} />
      {editable && (
        <MetadataTreeEditableArea
          metadataType={metadataType}
          secondaryMetadataType={secondaryMetadataType}
          addNode={addNode}
          replaceNode={replaceNode}
          moveNodes={moveNodes}
          mainTreeData={treeData}
          supportingTreeData={supportingTreeData}
          isSupportingTreeShown={isSupportingTreeShown}
          isReplaceActive={isReplaceActive}
          setIsReplaceActive={setIsReplaceActive}
          isMoveActive={isMoveActive}
          setIsMoveActive={setIsMoveActive}
          allowReplaceMany={allowReplaceMany}
          flatMainTreeData={flatMainTreeData}
          flatSupportingTreeData={flatSupportingTreeData}
          mainTreeCheckedKeys={mainTreeCheckedKeys}
          setMainTreeCheckedKeys={setMainTreeCheckedKeys}
          supportingTreeCheckedKeys={supportingTreeCheckedKeys}
          setSupportingTreeCheckedKeys={setSupportingTreeCheckedKeys}
          onSupportingTreeExpand={onSupportingTreeExpand}
          shouldPickSelectedSupportingItem={shouldPickSelectedSupportingItem}
        />
      )}
      <Row justify="space-between">
        <Col span={12}>
          <SearchableMetadataTree
            showSearch={showSearch}
            checkable={isSupportingTreeShown}
            checkStrictly
            checkedKeys={mainTreeCheckedKeys}
            onCheck={handleMainTreeOnCheck}
            onExpand={onMainTreeExpand}
            expandedKeys={mainTreeExpandedKeys}
            titleRender={renderMetadataTreeNode}
            treeData={treeData}
            blockNode
            flatTreeData={flatMainTreeData}
          />
        </Col>
        {isSupportingTreeShown && (
          <Col span={12}>
            <SearchableMetadataTree
              showSearch={showSearch}
              checkable
              checkStrictly
              checkedKeys={supportingTreeCheckedKeys}
              onCheck={handleSupportingTreeOnCheck}
              onExpand={onSupportingTreeExpand}
              expandedKeys={supportingTreeExpandedKeys}
              titleRender={renderMetadataTreeNode}
              treeData={supportingTreeData}
              blockNode
              flatTreeData={flatSupportingTreeData}
            />
          </Col>
        )}
      </Row>
    </StyledVerticalSpace>
  )
}

MetadataTree.propTypes = {
  disableLink: PropTypes.bool,
  mainTreeExpandedKeys: PropTypes.arrayOf(PropTypes.string),
  supportingTreeExpandedKeys: PropTypes.arrayOf(PropTypes.string),
  onMainTreeExpand: PropTypes.func,
  onSupportingTreeExpand: PropTypes.func,
  metadataType: PropTypes.object.isRequired,
  secondaryMetadataType: PropTypes.object,
  isDirty: PropTypes.bool,
  treeData: PropTypes.arrayOf(nodePropType),
  showSearch: PropTypes.bool,
  editable: PropTypes.bool,
  addNode: PropTypes.func,
  deleteNode: PropTypes.func,
  renameNode: PropTypes.func,
  setDataForRenaming: PropTypes.func,
  setIsCurationEnabled: PropTypes.func,
  dataForRenaming: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  allowReplaceMany: PropTypes.bool,
  moveNodes: PropTypes.func,
  replaceNode: PropTypes.func,
  revertReplaceNode: PropTypes.func,
  computeNodeHighlight: PropTypes.func,
  nodeTitleContentRender: PropTypes.func,
  showRenameInput: PropTypes.func,
  shouldPickSelectedSupportingItem: PropTypes.func,
  isMoveActive: PropTypes.bool,
  setIsMoveActive: PropTypes.func,
  isReplaceActive: PropTypes.bool,
  setIsReplaceActive: PropTypes.func,
  getLink: PropTypes.func,
}

MetadataTree.defaultProps = {
  secondaryMetadataType: undefined,
  disableLink: false,
  mainTreeExpandedKeys: [],
  supportingTreeExpandedKeys: [],
  onMainTreeExpand: noop,
  onSupportingTreeExpand: noop,
  isDirty: false,
  treeData: [],
  showSearch: false,
  editable: false,
  addNode: undefined,
  deleteNode: undefined,
  renameNode: undefined,
  setDataForRenaming: undefined,
  setIsCurationEnabled: undefined,
  dataForRenaming: undefined,
  allowReplaceMany: false,
  moveNodes: undefined,
  replaceNode: undefined,
  revertReplaceNode: undefined,
  computeNodeHighlight: undefined,
  nodeTitleContentRender: undefined,
  showRenameInput: undefined,
  shouldPickSelectedSupportingItem: undefined,
  isMoveActive: false,
  setIsMoveActive: noop,
  isReplaceActive: false,
  setIsReplaceActive: noop,
  getLink: undefined,
}

export default MetadataTree
